// @givmo/org-console — the Statistics block as View as public draws it (PE-WEB15; D-PE-41): Givmo's
// figures under the page's header, in the organization's order, two to a row — the strip both apps
// draw (iOS `PublicProfileContent` statistics/dollar rows, Android `companyStatRows`): a figure in
// Lato Bold 22 over its label, a short rule between the two in a row, a lone last figure across its
// row. So the default selection (the catalog's default order: two counts, then two amounts) is exactly
// today's strip.
//
// It draws only figures the public sees (the owner read's `public_view`, computed by the server for a
// signed-in person outside the organization — never this admin's own view). A stat that person does
// not see is never drawn, as a zero or a blank; the host's words for WHY sit under the strip as the
// preview's own dashed note, the idiom the preview already uses for what the public page shows that
// the console cannot. A figure whose key this build has no words for is not drawn either.

import { testIdProp } from '@givmo/design-system';
import {
  formatStatisticAmount,
  formatStatisticCount,
  joinLabels,
  statisticRows,
  type ProfileStatisticFigure,
  type ProfileStatisticWithheld,
} from './profile-statistics';
import type { ProfileStatisticsVocab } from './profile-statistics-card';

export interface ProfileStatisticsStripProps {
  figures: readonly ProfileStatisticFigure[];
  /** The chosen stats the public does not see, with why — drawn as one note per reason. */
  withheld?: readonly ProfileStatisticWithheld[];
  vocab: ProfileStatisticsVocab;
  testIdPrefix: string;
}

export function ProfileStatisticsStrip({
  figures,
  withheld = [],
  vocab,
  testIdPrefix,
}: ProfileStatisticsStripProps) {
  const prefix = `${testIdPrefix}-statistics`;
  const drawn = figures.filter((figure) => vocab.labels[figure.key] !== undefined);
  const notes = withheldNotes(withheld, vocab);
  if (drawn.length === 0 && notes.length === 0) return null;
  return (
    <div className="flex flex-col gap-16" {...testIdProp(prefix)}>
      {drawn.length > 0 && (
        <div className="flex flex-col gap-16">
          {statisticRows(drawn).map((row) => (
            <div
              key={row.map((figure) => figure.key).join('+')}
              className="flex items-center"
              {...testIdProp(`${prefix}-row`)}
            >
              {row.flatMap((figure, index) => [
                ...(index > 0
                  ? [
                      <span
                        key={`${figure.key}-rule`}
                        aria-hidden
                        className="h-20 w-px shrink-0 bg-border"
                      />,
                    ]
                  : []),
                <div
                  key={figure.key}
                  className="flex min-w-0 flex-1 flex-col items-center gap-4 text-center"
                  data-stat-key={figure.key}
                  {...testIdProp(`${prefix}-figure-${figure.key}`)}
                >
                  <p className="font-sans text-s22 font-w700 tracking-givmo text-text-strong tabular-nums">
                    {/* A figure always carries one value (`readStatisticFigure`); never a zero. */}
                    {figure.count !== null
                      ? formatStatisticCount(figure.count)
                      : figure.amount !== null
                        ? formatStatisticAmount(figure.amount)
                        : null}
                  </p>
                  <p className="font-sans text-s14 tracking-givmo text-text-tertiary">
                    {vocab.labels[figure.key]}
                  </p>
                </div>,
              ])}
            </div>
          ))}
        </div>
      )}
      {notes.map((note) => (
        <p
          key={note.reason}
          className="rounded-all12 border border-dashed border-border px-16 py-10 text-center font-sans text-s13 leading-h15 tracking-givmo text-text-tertiary"
          {...testIdProp(`${prefix}-note-${note.reason}`)}
        >
          {note.text}
        </p>
      ))}
    </div>
  );
}

/** One note per withheld reason, naming that reason's stats in the order they are chosen. */
function withheldNotes(
  withheld: readonly ProfileStatisticWithheld[],
  vocab: ProfileStatisticsVocab,
): { reason: string; text: string }[] {
  const byReason = new Map<string, string[]>();
  for (const row of withheld) {
    const label = vocab.labels[row.key];
    if (label === undefined) continue;
    byReason.set(row.reason, [...(byReason.get(row.reason) ?? []), label]);
  }
  const notes: { reason: string; text: string }[] = [];
  for (const [reason, labels] of byReason) {
    const words = vocab.withheldPreview?.[reason];
    if (words) notes.push({ reason, text: words(joinLabels(labels), labels.length) });
  }
  return notes;
}
